"use client";

import type { MouseEvent, ReactNode } from "react";
import { REGION_PICK_EVENT, type RegionPickDetail } from "@/components/guide-public/guide-map-engine";

/**
 * A link to a region on the real map. When the map is on the page it takes
 * the pick without a reload (the explorer listens for the event), the
 * address is updated, and the page scrolls to the map; elsewhere, or
 * without scripting, the link just works.
 */
export function RegionLink({
  slug,
  href,
  className,
  style,
  children,
  as = "a",
}: {
  slug: string;
  href: string;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
  /** "svg" for a link inside the drawing (an SVG <a> takes no className styling of its own). */
  as?: "a" | "svg";
}) {
  const pick = (event: MouseEvent<SVGAElement | HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const detail: RegionPickDetail = { slug, handled: false };
    window.dispatchEvent(new CustomEvent(REGION_PICK_EVENT, { detail }));
    if (!detail.handled) return;
    event.preventDefault();
    const url = new URL(window.location.href);
    url.searchParams.set("region", slug);
    url.hash = "map";
    window.history.replaceState(window.history.state, "", url);
    document.getElementById("map")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  if (as === "svg") {
    return (
      <a href={href} onClick={pick} className={className}>
        {children}
      </a>
    );
  }
  return (
    <a href={href} onClick={pick} className={className} style={style}>
      {children}
    </a>
  );
}
